import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { classnames } from 'tailwindcss-classnames';
import Button from 'components/helpers/Button';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination, Navigation } from 'swiper';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import { useEffect, useState } from 'react';
import useScreenWidth from 'lib/util/useScreenWidth';
import {
  gtmEcommerceDatalayerPush,
  OnLinkDataLayerPushProps,
  pushCarouselClickDataLayerObject,
} from 'lib/util/gtm-datalayer';

SwiperCore.use([Pagination, Navigation]);

type RelatedProductItemParams = Sitecore.Override.ItemEx & {
  fields: {
    relatedProductImageUrl?: string;
    pageTitle?: string;
    productNumber?: string;
  };
};

type RelatedProductsParams = {
  relatedProduct: RelatedProductItemParams[];
};
function renderProductImages(relatedProduct: RelatedProductItemParams) {
  const nextImageField = {
    value: {
      src: relatedProduct?.fields?.relatedProductImageUrl,
      alt: relatedProduct?.name,
      height: '241',
      width: '181',
    },
  };

  const pushGtm = ({ preventDefault }: OnLinkDataLayerPushProps) => {
    gtmEcommerceDatalayerPush({
      event: 'select_item',
      ecommerce: {
        value: '0',
        currency: 'USD',
        items: [
          {
            item_id: relatedProduct?.fields?.productNumber ?? relatedProduct.id,
            item_name: relatedProduct?.fields?.pageTitle,
            affiliation: 'N/A',
            coupon: 'N/A',
            discount: 'N/A',
            index: 'N/A',
            item_brand: 'NMDP',
            item_list_name: 'order_materials',
            item_variant: 'N/A',
            price: 0,
            quantity: 1,
          },
        ],
      },
    });
    preventDefault();
  };

  return (
    <>
      <div className={classnames(`product-image`)}>
        <ImageWrapper field={nextImageField} />
      </div>
      <Button
        linkField={{
          value: {
            href: relatedProduct?.url,
            text: relatedProduct?.fields?.pageTitle,
          },
        }}
        text={relatedProduct?.fields?.pageTitle}
        variant="link"
        onGtmPush={pushGtm}
        className={classnames(
          'inline-block',
          'font-regular',
          'leading-4',
          'text-blue',
          'md:mb-[3px]'
        )}
      />
      {relatedProduct?.fields?.productNumber && <p>{relatedProduct?.fields?.productNumber}</p>}
    </>
  );
}

const numberOfCardsForScreen = (screenWidth: number): number => {
  if (screenWidth > 1180) {
    return 6;
  }
  if (screenWidth > 580) {
    return 4;
  }
  return 2;
};

const RelatedProducts = ({ relatedProduct }: RelatedProductsParams): JSX.Element => {
  const screenWidth = useScreenWidth();
  const [cardsToDisplay, setCardsToDisplay] = useState(() => numberOfCardsForScreen(screenWidth));

  useEffect(() => {
    const newCardsToDisplay = numberOfCardsForScreen(screenWidth);
    setCardsToDisplay(newCardsToDisplay);
  }, [screenWidth]);

  if (!relatedProduct?.length) {
    return <></>;
  }

  const swiperControlsClasses = classnames(
    'm-auto',
    'my-4',
    'flex',
    'h-6',
    'sm:w-max',
    'items-center',
    'lg:mb-0',
    'max-w-[300px]'
    // Applies hidden-class for swiper control buttons for md-size page and higher
    // if count of cards for displaying on swiper not less than count of cards in collection.
  );
  const loopSwiper = true;
  const shouldShowSwiper =
    (screenWidth >= 1180 && relatedProduct.length > 6) ||
    (screenWidth >= 580 && screenWidth < 1180 && relatedProduct.length > 4) ||
    (screenWidth < 580 && relatedProduct.length > 2);

  let clickAction = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRealIndexChange = async (swiper: any) => {
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((r) => setTimeout(r, 200));
    pushCarouselClickDataLayerObject(
      swiper.realIndex,
      clickAction,
      'related products carousel',
      relatedProduct
    );
    clickAction = '';
  };
  const handlePrev = () => {
    clickAction = 'carousel - left click';
  };
  const handleNext = () => {
    clickAction = 'carousel - right click';
  };

  // List of Images
  return (
    <>
      <div className={classnames('flex', 'mb-6', 'lg:mb-10')}>
        <div className={classnames('mr-[11px]', 'h-[30px]', 'w-1', 'bg-[#8A2066]', 'md:h-9')} />
        <h3
          className={classnames(
            'my-auto',
            'font-lora',
            'text-[26px]',
            'leading-[30px]',
            'text-[#00355E]',
            'md:text-[32px]',
            'md:leading-9'
          )}
        >
          Related products ({relatedProduct?.length})
        </h3>
      </div>
      {/* Data that needs to be inside Swiper */}
      {shouldShowSwiper ? (
        <Swiper
          loop={loopSwiper}
          loopedSlides={cardsToDisplay}
          slidesPerView={cardsToDisplay}
          slidesPerGroup={1}
          breakpoints={{
            1024: {
              slidesPerView: cardsToDisplay,
              slidesPerGroup: cardsToDisplay,
            },
          }}
          spaceBetween={30}
          navigation={{
            prevEl: '.swiper-button-prev',
            nextEl: '.swiper-button-next',
          }}
          pagination={{
            clickable: true,
            el: '.swiper-pagination',
            bulletElement: 'button',
          }}
          onRealIndexChange={handleRealIndexChange}
        >
          <>
            {relatedProduct?.map((item) => {
              return (
                <div className={classnames('flex', 'justify-between')} key={item?.id}>
                  <SwiperSlide key={item?.id}>
                    <div
                      className={classnames(
                        'mt-2',
                        'flex',
                        'flex-col',
                        'lg:w-[181px]',
                        'lg:h-[322px]'
                      )}
                    >
                      {renderProductImages(item)}
                    </div>
                  </SwiperSlide>
                </div>
              );
            })}
          </>

          <div slot="container-end" className={classnames('mt-16')}>
            <div className={swiperControlsClasses}>
              <button type="button" className="swiper-button-prev" onClick={handlePrev}>
                prev
              </button>
              <div className="swiper-pagination" />
              <button type="button" className="swiper-button-next" onClick={handleNext}>
                next
              </button>
            </div>
          </div>
        </Swiper>
      ) : (
        <div className={classnames('flex', 'gap-6')}>
          {' '}
          {relatedProduct?.map((item) => {
            return (
              <div className={classnames('flex', 'justify-between')} key={item?.id}>
                <div key={item?.id}>
                  <div
                    className={classnames(
                      'mt-2',
                      'flex',
                      'flex-col',
                      'lg:w-[181px]',
                      'lg:h-[auto]'
                    )}
                  >
                    {renderProductImages(item)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default RelatedProducts;
